import { LoadingButton } from '@mui/lab';
import { Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { imageModeuleUrl, userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';
import { FormProvider, RHFUploadAvatar } from '../hook-form';
import LinearProgressBar from '../progress-bar/progressBar.component';

const WorkPlaceBox = ({ handleClose, handleSuccess }) => {
  const orgDetails = CommonHelper.GetBrandInfoFromLocalStorage('initial-load');
  const maxFileSizeInBytes = 10485760;
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();

  const [imageUrl, setImageUrl] = useState('');
  const [fileUploadprogress, setFileUploadprogress] = useState(0);
  const [brandName, setBrandName] = useState('');
  const [description, setDescription] = useState('');
  const [formSchema, setFormSchema] = useState({});
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [timeZone, setTimeZone] = useState('UTC');

  const methods = useForm({
    defaultValues: { imageUrl: '', description: '', brandName: '' },
  });
  const { handleSubmit } = methods;

  useEffect(() => {
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (localTimeZone) setTimeZone(localTimeZone);
    const fields = [ValidationTool.GenerateSchema('brandName', translate('brand.brand_name'), 'TEXT', true)];
    setFormSchema(ValidationTool.GenerateSchemaModel(fields, translate));
  }, []);

  const successCallback = (data, message) => {
    setLoading(false);
    if (message) showToast(message, 's');
    handleSuccess();
  };

  const failureCallback = (message) => {
    setLoading(false);
    if (message) showToast(message, 'e');
  };

  const createNewBrand = () => {
    HttpServices.Post({
      url: userModuleURL.createNewBrand,
      body: {
        brand_name: brandName,
        description,
        brand_image: imageUrl,
        timezone: timeZone,
        language: orgDetails?.language || 'english',
        brand_limit_info: {
          social_account_limit: 0,
          users_limit: 0,
          ai_content_tokens: 0,
        },
      },
      successCallback,
      failureCallback,
    });
  };

  const handleSubmitForm = () => {
    const { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, { brandName });
    if (!isValidForm) {
      setFormValidationErrors(formValidationErrors);
      return;
    }
    setLoading(true);
    setFormValidationErrors({});
    createNewBrand();
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    const name = event.target.name.toLowerCase();
    if (name === 'brandname') setBrandName(value);
    if (name === 'description') setDescription(value);
  };

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    const isFileInCorrectFormat = CommonHelper.SupportedImageFormat.some((a) => a.includes(file.type));
    if (!isFileInCorrectFormat) {
      showToast(`${translate('common.we_only_support')} JPEG, ${translate('common.and')} PNG ${translate('common.file_formats')}.`, 'e');
      return;
    }
    if (file.size >= maxFileSizeInBytes) {
      showToast(`${translate('brand.max_image_size')} 10MB. ${translate('brand.please_select_below')}`, 'e');
      return;
    }
    setFileUploadprogress(1);
    const formData = new FormData();
    formData.append('fileupload', file);
    axios
      .post(imageModeuleUrl.uploadMedia, formData, {
        headers: HttpServices.GetRequestHeader('form', true),
        onUploadProgress: (data) => {
          const loadedProgress = Math.round((100 * data.loaded) / data.total);
          setFileUploadprogress(loadedProgress > 90 ? 90 : loadedProgress);
        },
      })
      .then((response) => {
        setFileUploadprogress(100);
        if (response.status === 200 && response.data.http_code === 200) {
          const mediaUrl = response.data?.data?.media_data?.[0]?.media_url;
          if (mediaUrl) setImageUrl(mediaUrl);
        } else {
          showToast(translate('common.something_went_wrong'), 'e');
        }
        setFileUploadprogress(0);
      })
      .catch((error) => {
        setFileUploadprogress(0);
        showToast(error.toString(), 'e');
      });
  };

  return (
    <Dialog open maxWidth="md" fullWidth>
      <FormProvider methods={methods} onSubmit={handleSubmit(handleSubmitForm)}>
        <DialogTitle>
          <Typography variant="h4">{translate('brand.create_brand')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Card sx={{ p: 1, textAlign: 'center', mb: 2 }}>
            <RHFUploadAvatar name="imageUrl" accept="image/*" onDrop={handleDrop} handleDeleteProfiles={() => setImageUrl('')} url={imageUrl} />
          </Card>
          {fileUploadprogress > 0 && <LinearProgressBar value={fileUploadprogress} />}
          <Stack spacing={3} sx={{ mt: 2, mx: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label={translate('brand.brand_name')} name="brandName" value={brandName} fullWidth onChange={handleInputChange} error={!!formValidationErrors?.brandName} helperText={formValidationErrors?.brandName || ' '} />
              </Grid>
              <Grid item xs={12}>
                <TextField label={translate('common.description')} name="description" value={description} multiline minRows={2} fullWidth onChange={handleInputChange} />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleClose}>
            {translate('common.cancel')}
          </Button>
          <LoadingButton variant="contained" loading={isLoading} type="submit">
            {translate('brand.create_brand')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default WorkPlaceBox;
